import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ShoppingCart, Crown } from 'lucide-react';
import TopBuyersCard from './TopBuyersCard';

/**
 * Aba Vendas do afiliado — sub-abas de histórico e top compradores.
 */
const SalesTab = ({ reseller, sales = [], salesFilter, setSalesFilter, onFilter }) => {
  return (
    <Tabs defaultValue="history" className="w-full">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="history" data-testid="subtab-sales-history">
          <ShoppingCart className="mr-2 h-4 w-4" /> Histórico
        </TabsTrigger>
        <TabsTrigger value="top-buyers" data-testid="subtab-top-buyers">
          <Crown className="mr-2 h-4 w-4" /> Top compradores
        </TabsTrigger>
      </TabsList>

      <TabsContent value="top-buyers" className="mt-4">
        <TopBuyersCard resellerId={reseller?.id} />
      </TabsContent>

      <TabsContent value="history" className="mt-4">
      <div className="space-y-3 mb-4">
        <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
          <div className="min-w-0">
            <Label className="text-xs">Data Início</Label>
            <Input
              type="date"
              value={salesFilter.start_date}
              onChange={(e) => setSalesFilter({ ...salesFilter, start_date: e.target.value })}
              className="w-full"
            />
          </div>
          <div className="min-w-0">
            <Label className="text-xs">Data Fim</Label>
            <Input
              type="date"
              value={salesFilter.end_date}
              onChange={(e) => setSalesFilter({ ...salesFilter, end_date: e.target.value })}
              className="w-full"
            />
          </div>
          <Button onClick={onFilter} className="col-span-2 sm:col-span-1 w-full sm:w-auto">
            Filtrar
          </Button>
        </div>
      </div>

      {sales.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <ShoppingCart className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p>Nenhuma venda ainda</p>
        </div>
      ) : (
        <>
          <div className="text-xs text-gray-500 mb-2 px-1">
            Mostrando <strong>{sales.length}</strong> venda(s) — role para ver mais
          </div>
          <div className="max-h-[500px] overflow-y-auto pr-1">
            {Object.entries(
              sales.reduce((groups, sale) => {
                if (!sale.completed_at) return groups;
                const date = sale.completed_at.split('T')[0];
                if (!groups[date]) groups[date] = [];
                groups[date].push(sale);
                return groups;
              }, {})
            ).map(([date, daySales]) => {
              const dayTotal = daySales.reduce((sum, s) => sum + (s.coins || 0), 0);
              return (
                <div key={date} className="mb-4 border rounded-lg overflow-hidden shadow-sm">
                  <div className="bg-gradient-to-r from-purple-600 to-purple-500 text-white px-3 py-2 flex items-center justify-between gap-2">
                    <h4 className="font-bold text-sm whitespace-nowrap">
                      📅 {date.split('-').reverse().join('/')}
                    </h4>
                    <span className="text-[10px] sm:text-xs bg-white/20 rounded px-2 py-0.5 font-semibold text-right">
                      {daySales.length} venda(s) • {dayTotal.toLocaleString()} 🪙
                    </span>
                  </div>

                  {/* MOBILE */}
                  <div className="md:hidden divide-y">
                    {daySales.map((sale, idx) => (
                      <div key={idx} className="p-3 hover:bg-purple-50/40">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 text-[11px] text-gray-500">
                              <span className="font-mono" title={sale.order_id || ''}>
                                #{sale.order_id ? sale.order_id.slice(0, 8) : '-'}
                              </span>
                              <span>•</span>
                              <span className="font-medium">
                                {sale.completed_at ? new Date(sale.completed_at).toLocaleTimeString('pt-BR') : '-'}
                              </span>
                            </div>
                            <div className="mt-1 text-sm">
                              {reseller?.can_view_customers ? (
                                <div className="min-w-0">
                                  {sale.nickname && (
                                    <p className="font-medium text-gray-900 truncate">{sale.nickname}</p>
                                  )}
                                  <p className="text-[11px] text-gray-500 font-mono">ID: {sale.kako_id || '-'}</p>
                                </div>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs text-gray-500 font-semibold">
                                  🔒 <span className="blur-sm select-none pointer-events-none">{sale.kako_id || '00000000'}</span>
                                </span>
                              )}
                            </div>
                          </div>
                                  <div className="text-right shrink-0">
                                    <p className="font-bold text-purple-600 text-base whitespace-nowrap">
                                      {(sale.coins || 0).toLocaleString()} 🪙
                                    </p>
                                    {sale.commission_coins > 0 && (
                                      <p className="text-[11px] font-semibold text-emerald-600 whitespace-nowrap mt-0.5">
                                        +{sale.commission_coins.toLocaleString()} 💰
                                      </p>
                                    )}
                                  </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* DESKTOP */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left p-2 font-semibold text-gray-600">Pedido</th>
                          <th className="text-left p-2 font-semibold text-gray-600">Hora</th>
                          <th className="text-left p-2 font-semibold text-gray-600">ID Cliente</th>
                          <th className="text-right p-2 font-semibold text-gray-600">Moedas</th>
                          <th className="text-right p-2 font-semibold text-gray-600">Comissão</th>
                        </tr>
                      </thead>
                      <tbody>
                        {daySales.map((sale, idx) => (
                          <tr key={idx} className="border-b last:border-b-0 hover:bg-purple-50/40 transition-colors">
                            <td className="p-2">
                              <span className="font-mono text-[11px] text-gray-700" title={sale.order_id || ''}>
                                {sale.order_id ? sale.order_id.slice(0, 8) : '-'}
                              </span>
                            </td>
                            <td className="p-2 text-gray-600 font-medium whitespace-nowrap">
                              {sale.completed_at ? new Date(sale.completed_at).toLocaleTimeString('pt-BR') : '-'}
                            </td>
                            <td className="p-2">
                              {reseller?.can_view_customers ? (
                                <div>
                                  {sale.nickname && (
                                    <p className="font-medium text-gray-900">{sale.nickname}</p>
                                  )}
                                  <p className="text-[11px] text-gray-500 font-mono">{sale.kako_id || '-'}</p>
                                </div>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs text-gray-500 font-semibold">
                                  🔒 <span className="blur-sm select-none pointer-events-none">{sale.kako_id || '00000000'}</span>
                                </span>
                              )}
                            </td>
                            <td className="p-2 text-right font-bold text-purple-600 whitespace-nowrap">
                              {(sale.coins || 0).toLocaleString()} 🪙
                            </td>
                            <td className="p-2 text-right whitespace-nowrap">
                              {sale.commission_coins > 0 ? (
                                <span className="font-semibold text-emerald-600">
                                  +{sale.commission_coins.toLocaleString()} 💰
                                </span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      </TabsContent>
    </Tabs>
  );
};

export default SalesTab;
